import { can } from '@misumi/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LeftOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/ToastProvider'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { useAuth } from '../../lib/auth/auth-context'
import { combinedItemCount, useCartCount } from '../../lib/cart/cart-count-context'
import { GenerateQuoteModal } from '../cart/GenerateQuoteModal'
import { describeCheckoutSplit } from '../cart/cart-readiness'
import { AddShippingAccountModal } from './AddShippingAccountModal'
import { CheckoutSection } from './CheckoutSection'
import { CheckoutSummaryRail } from './CheckoutSummaryRail'
import { DeliveryPlanStep } from './DeliveryPlanStep'
import { OrderPlacedCelebration } from './OrderPlacedCelebration'
import { type PaymentSelectionMode, PaymentStep } from './PaymentStep'
import { ShippingAddressStep } from './ShippingAddressStep'
import { ShippingMethodStep } from './ShippingMethodStep'
import {
  type AchFormFields,
  type AddressFormFields,
  type AddressView,
  type CardFormFields,
  type CheckoutRequest,
  type CheckoutShippingSeed,
  type CheckoutView,
  type DeliveryPlanRates,
  type FieldErrors,
  type LineItem,
  type PaymentMethodKind,
  type PaymentSelection,
  type PlaceOrdersRequest,
  type PlaceOrdersResult,
  type PoBreakdownGroup,
  type ShipMode,
  type ShippingAccountFormFields,
  type ShippingAccountView,
  buildOrderGroups,
  classifyCheckoutPlacementError,
  defaultShippingAccountId,
  derivePoGroups,
  firstNonBlankPoReference,
  generateSimulatedTokenRef,
  maskedDisplayLabel,
  placeOrderCtaLabel,
  placingPurchaseOrdersNote,
  resolveSeededShipToAddressId,
  resolveSeededShippingMethodId,
  resolveShowDeliveryPlan,
  toCreateAddressRequest,
  toCreateShippingAccountRequest,
  toUpdateAddressRequest,
  validateAchFields,
  validateCardFields,
  validatePoFields,
  validatePoGroupNumbers,
} from './checkout-domain'

type Order = components['schemas']['Order']
// GET /cart's actual declared response (operations['getCurrentCart']) is
// CartViewWithLineSpecifications, a superset of the base CartView — widened
// here (was CartView) so `lineThumbnails` (already present on every real
// response, just previously untyped on this page) is available for the
// delivery-plan step's shipment-row thumbnails, the same field CartPage.tsx
// already reads for its own line thumbnails.
type CartView = components['schemas']['CartViewWithLineSpecifications']

const CARD_FIELD_ORDER: readonly (keyof CardFormFields)[] = [
  'cardNumber',
  'expiry',
  'cvc',
  'nameOnCard',
  'billingZip',
]
const ACH_FIELD_ORDER: readonly (keyof AchFormFields)[] = [
  'routingNumber',
  'accountNumber',
  'accountType',
]

/**
 * UID-60 — the Checkout screen (`/checkout`, WS-5), gated `order:submit`
 * (Purchaser-only in production, ADR-0038 §2; also granted to `engineer` as a
 * DEMO-ONLY override, `packages/shared/src/authz.ts` — not a production RBAC
 * decision). Stage-2b (ADR-0040/single-page redesign):
 * ALL THREE numbered sections (shipping address, shipping method, payment)
 * render fully expanded on one page — no accordion/stepper, no
 * "Continue"/"Edit" affordances — beside a sticky order-summary rail, backed
 * by the Stage-2a `CheckoutView` (`GET /checkout`): saved ship-to addresses,
 * a shipping-method rate table, saved tokenized payment methods, and a
 * server-authoritative summary. Selecting a §1/§2 option re-fetches
 * `CheckoutView` under that id so the summary recalculates server-side
 * (ADR-0029 — never client-recomputed). `POST /api/orders` places the order
 * with the new id-based-or-explicit `CheckoutRequest` body, then the
 * order-placed celebration (unchanged, `OrderPlacedCelebration.tsx`).
 *
 * SIMULATED TOKENIZATION (the PCI boundary, UX spec §2.3.4 / ADR-0038 §5):
 * this component holds raw NEW-card/NEW-ACH field values ONLY in local React
 * state for exactly as long as it takes to derive a `sim_tok_*` reference on
 * submit (`generateSimulatedTokenRef`, checkout-domain.ts). A SAVED payment
 * method is instead referenced purely by id (`savedPaymentMethodId`) — its
 * SIMULATED token is resolved server-side (Stage-2a `resolveCheckoutSelections`)
 * and never touches this component at all. Either way, the raw values are
 * NEVER placed in the `CheckoutRequest` body, NEVER written to
 * `localStorage`/any persisted store, and never passed to `showToast` or any
 * error path — only `payment.tokenRef` (opaque) and `payment.displayLabel`
 * (last-4 only, `maskedDisplayLabel`) leave this component. `po_invoice`
 * sends its PO number directly (not sensitive payment data, no tokenization
 * needed).
 */
export function CheckoutPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()

  const isAuthorized =
    auth.status === 'authenticated' && can(auth.session.accountContext.role, 'order:submit')

  const [liveMessage, setLiveMessage] = useState('')

  // ---- CheckoutView (§1/§2 selections + server-derived summary) ----
  const [checkoutView, setCheckoutView] = useState<CheckoutView>()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string>()
  const [selectionBusy, setSelectionBusy] = useState(false)
  const [selectedAddressId, setSelectedAddressId] = useState<string>()
  const [selectedMethodId, setSelectedMethodId] = useState<string>()

  // ---- §3 payment eligibility (advisory, ADR-0024/0038) ----
  const [eligibilityStatus, setEligibilityStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  )
  const [allowedMethods, setAllowedMethods] = useState<readonly PaymentMethodKind[]>([
    'card',
    'ach',
  ])
  const [paymentKind, setPaymentKind] = useState<PaymentMethodKind>('card')
  const [paymentMode, setPaymentMode] = useState<PaymentSelectionMode>('new')
  const [selectedSavedPaymentId, setSelectedSavedPaymentId] = useState<string>()

  const [card, setCard] = useState<CardFormFields>({
    cardNumber: '',
    expiry: '',
    cvc: '',
    nameOnCard: '',
    billingZip: '',
  })
  const [cardErrors, setCardErrors] = useState<FieldErrors>({})
  const [cardTouched, setCardTouched] = useState<ReadonlySet<string>>(new Set())

  const [ach, setAch] = useState<AchFormFields>({
    routingNumber: '',
    accountNumber: '',
    // Design defaults the account type to Checking (prototype `ach.type:'checking'`).
    accountType: 'checking',
  })
  const [achErrors, setAchErrors] = useState<FieldErrors>({})
  const [achTouched, setAchTouched] = useState<ReadonlySet<string>>(new Set())

  const [poNumber, setPoNumber] = useState('')
  const [poErrors, setPoErrors] = useState<FieldErrors>({})
  const [poTouched, setPoTouched] = useState(false)

  const [termsAccepted, setTermsAccepted] = useState(false)
  const termsCheckboxRef = useRef<HTMLInputElement>(null)

  // ---- Generate quote (UID-139) — the rail's "Generate quote" CTA opens the
  // SAME rich quote modal the cart screen uses (`GenerateQuoteModal`), matching
  // the prototype where checkout's `co.onGenerateQuote` also calls `openQuote()`
  // (`Fictiv Platform.dc.html:2135` / `7217`). `generatingQuote` now only covers
  // the brief `GET /cart` fetch that populates the modal's `CartView`; the
  // confirm → generating → ready flow lives entirely inside the modal. ----
  const [generatingQuote, setGeneratingQuote] = useState(false)
  const [showGenerateQuote, setShowGenerateQuote] = useState(false)
  const [quoteCart, setQuoteCart] = useState<CartView>()

  // ---- Placement ----
  const [submitting, setSubmitting] = useState(false)
  const [placedOrder, setPlacedOrder] = useState<Order>()
  const [placementBanner, setPlacementBanner] = useState<string>()
  // Gate 5 W2 — set alongside `placementBanner` ONLY for the batch
  // double-submit `no_ready_lines` case, so the banner can link the buyer
  // to Recent Orders instead of just describing the (misleading) error.
  const [placementBannerShowOrdersLink, setPlacementBannerShowOrdersLink] = useState(false)
  const [placedPoBreakdown, setPlacedPoBreakdown] = useState<readonly PoBreakdownGroup[]>()

  // ---- UID-97 K2 — saved address book (re-sourced from /account/addresses,
  // ADR-0044 §2 — carries the K4 contactName/companyName the checkoutView's
  // own `shipToAddresses` does NOT). This list, not `checkoutView
  // .shipToAddresses`, drives §1's rendered cards; `selectedAddressId` (and
  // the summary's tax line) still comes from `checkoutView` — same ids,
  // same tenant. ----
  const [addresses, setAddresses] = useState<readonly AddressView[]>([])

  // ---- UID-206 — the saved shipping-account book (own-account carrier book,
  // checkout §2). `selectedShippingAccountId` is the row the buyer picks; it is
  // sent as `CheckoutRequest.shippingAccountId` at placement ONLY when the
  // own-account method is selected (the server resolves it into the frozen
  // carrier/ref). Defaults to the book's default account on load. ----
  const [shippingAccounts, setShippingAccounts] = useState<readonly ShippingAccountView[]>([])
  const [selectedShippingAccountId, setSelectedShippingAccountId] = useState<string | null>(null)
  const [showAddShippingAccount, setShowAddShippingAccount] = useState(false)

  // ---- UID-97 K3 — saved payment methods, overridable after a Remove so
  // the removed card leaves the list without a full checkout re-fetch. ----
  const [paymentMethodsOverride, setPaymentMethodsOverride] = useState<
    CheckoutView['savedPaymentMethods'] | undefined
  >()

  // ---- UID-97 K1 — split-PO / multi-PO (ADR-0044 §1) ----
  const [cartLines, setCartLines] = useState<readonly LineItem[]>([])
  // Set true ONLY when a `GET /cart` actually SUCCEEDS (data returned). The
  // empty-cart guard keys off this — never off a bare empty `cartLines` — so a
  // failed/401 cart fetch (non-fatal, ADR-0029) can never bounce the buyer off
  // checkout; only a confirmed-empty cart does.
  const [cartFetchSucceeded, setCartFetchSucceeded] = useState(false)
  // UID-315 (ADR-0106): the split-PO CHOICE now lives on the CART and is carried here via
  // the persisted `CartView.splitPoEnabled` — checkout is CHOICE-DRIVEN, with NO checkout
  // toggle (superseding the ADR-0044 `CheckoutSummaryRail` Switch). `splitEnabled` is a
  // derived read of the cart's choice; it is still gated by `canSplit` (2+ ready groups) at
  // every downstream site, so a cart that chose split but resolves to one group falls back
  // to a single PO. The per-group PO-number entry + validation (`poGroupNumbers`) STAY.
  const [cartSplitPoEnabled, setCartSplitPoEnabled] = useState(false)
  const splitEnabled = cartSplitPoEnabled
  const [poGroupNumbers, setPoGroupNumbers] = useState<Record<string, string>>({})
  // Gate 5 C1 — unlike a non-PO-invoice split (where a group PO is
  // optional), EVERY group's PO is required when paying by PO invoice
  // (`validatePoGroupNumbers`); this surfaces which group(s) are missing one.
  const [poGroupErrors, setPoGroupErrors] = useState<FieldErrors>({})
  const poGroups = derivePoGroups(cartLines)
  const canSplit = poGroups.length > 1
  // UID-269 — checkout splits by readiness: this checkout places only the
  // READY lines (see `poGroups`/`derivePoGroups`, ready-only); any
  // pending/quote-required lines in a mixed cart stay bound to the cart and
  // append to it on acceptance — never part of this placement. Surfaced to
  // the buyer via the summary rail so the split is explicit, not silent.
  const readyLineCount = cartLines.filter((line) => line.status === 'ready').length
  const pendingLineCount = cartLines.length - readyLineCount
  const readinessSplitNote = describeCheckoutSplit(readyLineCount, pendingLineCount)

  // ---- Delivery-plan step (RFQ multi-lead-time carts) — a deliberate, scoped exception
  // to ADR-0106's "no checkout toggle" rule (see checkout-domain.ts's comment): this reads
  // + writes the SAME cart-owned `ShipPlan.mode` field the Cart screen's `ShipPlanCard`
  // already drives (`PUT /cart/ship-plan`), gated on `resolveShowDeliveryPlan` (an
  // RFQ-originated cart whose lines resolve to more than one distinct lead time). Reuses
  // the SAME `cartLines`/`quoteCart` this component already fetches for PO grouping — no
  // new endpoint. `quoteCart` (the full `CartView` stashed for the Generate-quote modal)
  // supplies the ship plan + per-line presentations this step needs to render dates/legs.
  const [deliveryPlanBusy, setDeliveryPlanBusy] = useState(false)
  const linePresentationsBySourceableId = quoteCart?.linePresentations
    ? new Map(
        quoteCart.linePresentations.map((presentation) => [
          presentation.sourceableId,
          presentation,
        ]),
      )
    : undefined
  // UID-94/UID-617 — `resolveShowDeliveryPlan` needs the presentation join to tell a
  // FRESH RFQ line apart from a REORDERED one (`sourceOrderId` lives on
  // `CartLinePresentation`, not the shared `LineItem` — see its own doc comment).
  const showDeliveryPlan = resolveShowDeliveryPlan(cartLines, linePresentationsBySourceableId)
  // Mirrors CartPage.tsx's own `thumbnailsBySourceableId` construction — the
  // SAME `lineThumbnails` field on the SAME `GET /cart` response, just not
  // previously read on this page.
  const thumbnailsBySourceableId = new Map(
    (quoteCart?.lineThumbnails ?? []).map((entry) => [entry.sourceableId, entry.thumbnailUrl]),
  )

  // Computed section numbering (never hardcoded literals) — §1 is always the shipping
  // address; the delivery-plan step, when shown, occupies §2 and pushes §2 shipping
  // method / §3 payment down by one.
  const addressStepNumber = 1
  const deliveryPlanStepNumber = 2
  const shippingMethodStepNumber = showDeliveryPlan ? 3 : 2
  const paymentStepNumber = showDeliveryPlan ? 4 : 3

  // ---- UID-128 (ADR-0053 §7 follow-up) — checkout shipping pre-fill FROM the cart's
  // originating RFQ (`GET /checkout/shipping-seed`). `shippingSeed` holds the fetched
  // result (or `undefined` while in flight / on failure — see `loadShippingSeed`), fetched
  // in PARALLEL with `loadCheckout` (Gate 5 QA fix — the seed fetch must never delay or
  // block checkout's render; see the mount effect below). `methodExplicitlySelectedRef` /
  // `addressExplicitlySelectedRef` flip `true` the moment the customer makes their OWN §2
  // / §1 pick — the seed-application effect checks these per-field so the customer's own
  // selection ALWAYS wins over the seed, even if the seed (now fetched independently) is
  // slow and resolves AFTER that pick. `seedAppliedRef` ensures the seed is applied at
  // most once per mount (never re-applied just because our own re-fetch updates
  // `checkoutView` again). ----
  const [shippingSeed, setShippingSeed] = useState<CheckoutShippingSeed>()
  // UID-617 — the DeliveryPlanStep's two cards' STABLE figures, refetched whenever the §1/§2
  // selection changes (the "together" figure tracks the selected method) but NEVER by a
  // ship-plan mode switch itself — that's the whole point (see `deriveDeliveryPlanRates`).
  const [deliveryPlanRates, setDeliveryPlanRates] = useState<DeliveryPlanRates>()
  const methodExplicitlySelectedRef = useRef(false)
  const addressExplicitlySelectedRef = useRef(false)
  const seedAppliedRef = useRef(false)

  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const registerFieldRef = useCallback(
    (name: string) => (el: HTMLInputElement | null) => {
      fieldRefs.current[name] = el
    },
    [],
  )

  function focusFirstError(order: readonly string[], errors: FieldErrors) {
    for (const name of order) {
      if (errors[name]) {
        fieldRefs.current[name]?.focus()
        return
      }
    }
  }

  function announceErrors(errors: FieldErrors) {
    const messages = Object.values(errors).filter((value): value is string => Boolean(value))
    if (messages.length === 1) {
      setLiveMessage(messages[0] as string)
    } else if (messages.length > 1) {
      setLiveMessage(`${messages.length} fields need attention.`)
    }
  }

  /** Default the payment-selection mode/id for `kind` from the view's saved
   * methods — "saved" pre-selecting the first matching saved method when any
   * exist, else "new" (there is nothing to default to). */
  const defaultPaymentSelectionFor = useCallback((view: CheckoutView, kind: PaymentMethodKind) => {
    const firstSaved = view.savedPaymentMethods.find((method) => method.kind === kind)
    if (firstSaved) {
      setPaymentMode('saved')
      setSelectedSavedPaymentId(firstSaved.id)
    } else {
      setPaymentMode('new')
      setSelectedSavedPaymentId(undefined)
    }
  }, [])

  const loadCheckout = useCallback(
    async (query?: {
      shipToAddressId?: string | undefined
      shippingMethodId?: string | undefined
    }) => {
      // `exactOptionalPropertyTypes` rejects an explicit `key: undefined` on
      // the generated query-param type — omit unset keys entirely rather
      // than passing them through as `undefined`.
      const cleanQuery: { shipToAddressId?: string; shippingMethodId?: string } = {}
      if (query?.shipToAddressId !== undefined) cleanQuery.shipToAddressId = query.shipToAddressId
      if (query?.shippingMethodId !== undefined) {
        cleanQuery.shippingMethodId = query.shippingMethodId
      }
      try {
        const { data, error } = await apiClient.GET('/checkout', {
          params: { query: cleanQuery },
        })
        if (data) {
          setCheckoutView(data)
          setSelectedAddressId(data.selectedShipToAddressId)
          setSelectedMethodId(data.selectedShippingMethodId)
          return data
        }
        if (error?.status === 401) return undefined // Handled globally.
        setLoadError(error?.detail ?? "Couldn't load checkout. Please try again.")
        return undefined
      } catch {
        setLoadError('Something went wrong. Please try again.')
        return undefined
      }
    },
    [],
  )

  // ---- UID-128 (ADR-0053 §7 follow-up) — the checkout shipping SEED derived
  // server-side FROM the cart's originating RFQ (`GET /checkout/shipping-seed`, a
  // frontend-local BFF endpoint, `cart:manage`). ALL security/tenant validation — the
  // tenant-scoped RFQ read, the shipToAddressId set-membership/IDOR gate — runs
  // server-side (see the backend UID-128 commits); this just consumes the result. A
  // failure (401/5xx/network) resolves to `undefined`, meaning "nothing to seed" —
  // exactly like a non-RFQ-originated cart — never a broken page. READ-ONLY: this GET
  // is the only call this feature ever makes; nothing here writes the RFQ. ----
  const loadShippingSeed = useCallback(async () => {
    try {
      const { data, error } = await apiClient.GET('/checkout/shipping-seed')
      if (data) return data
      if (error) return undefined
    } catch {
      return undefined
    }
    return undefined
  }, [])

  // UID-617 — same query shape as `loadCheckout`, so the "together" figure tracks whatever
  // method/address is actually selected. A failure resolves to `undefined`: the DeliveryPlanStep
  // then falls back to its own local approximation rather than a broken page.
  const loadDeliveryPlanRates = useCallback(
    async (query?: { shipToAddressId?: string; shippingMethodId?: string }) => {
      const cleanQuery: { shipToAddressId?: string; shippingMethodId?: string } = {}
      if (query?.shipToAddressId !== undefined) cleanQuery.shipToAddressId = query.shipToAddressId
      if (query?.shippingMethodId !== undefined) {
        cleanQuery.shippingMethodId = query.shippingMethodId
      }
      try {
        const { data, error } = await apiClient.GET('/checkout/delivery-plan-rates', {
          params: { query: cleanQuery },
        })
        if (data) return data
        if (error) return undefined
      } catch {
        return undefined
      }
      return undefined
    },
    [],
  )

  // ---- UID-97 K2 — the saved address book (re-sourced list, see the state
  // comment above) ----
  const loadAddresses = useCallback(async () => {
    try {
      const { data, error } = await apiClient.GET('/account/addresses')
      if (data) {
        setAddresses(data.addresses)
        return
      }
      if (error?.status === 401) return // Handled globally.
    } catch {
      // Non-fatal: §1 falls back to whatever `addresses` already held (empty
      // on first load) rather than crashing the whole checkout page.
    }
  }, [])

  // ---- UID-206 — the saved shipping-account book. Read-only load here; the
  // own-account §2 list renders from it and the buyer can add to it. Pre-selects
  // the book's default account so an own-account placement has a resolvable id
  // without an extra click. Non-fatal on error (the list just stays empty). ----
  const loadShippingAccounts = useCallback(async () => {
    try {
      const { data, error } = await apiClient.GET('/account/shipping-accounts')
      if (data) {
        setShippingAccounts(data.shippingAccounts)
        setSelectedShippingAccountId(
          (current) => current ?? defaultShippingAccountId(data.shippingAccounts),
        )
        return
      }
      if (error?.status === 401) return // Handled globally.
    } catch {
      // Non-fatal — the own-account list stays empty (only the "+ Add" affordance shows).
    }
  }, [])

  // ---- UID-97 K1 — the current cart's lines, to derive the PO groups.
  // Read-only here (checkout never mutates the cart); a fetch failure just
  // means the split-PO toggle stays unavailable (`canSplit` is false for an
  // empty `cartLines`), never a broken page. ----
  const loadCartLines = useCallback(async () => {
    try {
      const { data, error } = await apiClient.GET('/cart')
      if (data) {
        setCartLines(data.lines)
        // UID-315 (ADR-0106): carry the cart's persisted split-PO choice into checkout.
        setCartSplitPoEnabled(data.splitPoEnabled ?? false)
        // Stash the full CartView too — it's what the Generate-quote modal
        // needs (summary + ship-to addresses), so opening the quote reuses this
        // already-fetched cart instead of re-hitting `GET /cart`.
        setQuoteCart(data)
        // Sync the top-bar cart badge so it reflects the real cart while on
        // checkout (previously checkout never pushed the count, so the badge sat
        // at 0/blank here until placement). UID-423 (ADR-0127 §H): COMBINED
        // count (part lines + requested services), matching every other
        // push site.
        setLineCount(combinedItemCount(data))
        setCartFetchSucceeded(true)
        return
      }
      if (error?.status === 401) return // Handled globally.
    } catch {
      // See comment above.
    }
  }, [setLineCount])

  // ---- Post-placement cart settle. The server has already removed the
  // ordered (ready) lines from the cart (OrderService settles on placement),
  // so re-fetch the authoritative cart and sync BOTH the local `cartLines`
  // (stale after placement) and the global top-bar badge (`setLineCount`).
  // Any leftover PENDING lines are reflected accurately by using the fresh
  // `data.lines.length` rather than hardcoding 0. Non-fatal: the order is
  // already placed, so a failed refresh just leaves the badge to self-heal on
  // the next cart visit — it never blocks the success celebration. ----
  const refreshCartAfterPlacement = useCallback(async () => {
    try {
      const { data } = await apiClient.GET('/cart')
      if (data) {
        setCartLines(data.lines)
        // UID-423 (ADR-0127 §H): COMBINED count, matching every other push site.
        setLineCount(combinedItemCount(data))
      }
    } catch {
      // See comment above — best-effort, self-heals on next cart load.
    }
  }, [setLineCount])

  const loadEligibility = useCallback(async () => {
    setEligibilityStatus('loading')
    try {
      const { data, error } = await apiClient.GET('/checkout/payment-eligibility')
      if (data) {
        setAllowedMethods(data.allowedMethods)
        setEligibilityStatus('loaded')
        return
      }
      if (error?.status === 401) return // Handled globally.
      // Fail closed to the s0.12 default (card + ACH only) — never fail open.
      setAllowedMethods(['card', 'ach'])
      setEligibilityStatus('error')
    } catch {
      setAllowedMethods(['card', 'ach'])
      setEligibilityStatus('error')
    }
  }, [])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setLoadError(undefined)
    // Gate 5 QA fix (UID-128): `loadCheckout` alone gates `loading` — checkout renders as
    // soon as IT resolves. The shipping-seed fetch below runs fully in PARALLEL (fired in
    // this same effect body, not chained after loadCheckout's `.then`) and NEVER blocks or
    // delays this render: a non-RFQ cart (or a slow/hanging /checkout/shipping-seed) sees
    // checkout render exactly as promptly as it always has. The seed, if/when it arrives,
    // is applied by the separate effect below (once both it and `checkoutView` are ready).
    void loadCheckout().then((view) => {
      if (mounted && view) defaultPaymentSelectionFor(view, 'card')
      if (mounted) setLoading(false)
    })
    void loadEligibility()
    void loadAddresses()
    void loadShippingAccounts()
    void loadCartLines()
    void loadShippingSeed().then((seed) => {
      if (mounted) setShippingSeed(seed)
    })
    return () => {
      mounted = false
    }
  }, [
    loadCheckout,
    loadEligibility,
    loadAddresses,
    loadShippingAccounts,
    loadCartLines,
    loadShippingSeed,
    defaultPaymentSelectionFor,
  ])

  // Empty-cart guard: if a SUCCESSFUL `GET /cart` shows no ready-to-order lines,
  // there's nothing to place — send the buyer back to the cart instead of letting
  // them fill in payment and hit "no items ready" at Place order. SAFETY: gated on
  // `cartFetchSucceeded` (never a bare empty `cartLines`), so a failed/401 cart
  // fetch can't bounce them off checkout. Skipped during/after placement
  // (`submitting`/`placedOrder`) — the server settles the ordered lines out of the
  // cart on success, which must NOT read as "empty cart" and race the order-placed
  // navigation. One-shot ref so it can never loop.
  const emptyCartRedirectedRef = useRef(false)
  useEffect(() => {
    if (emptyCartRedirectedRef.current) return
    if (loading || !cartFetchSucceeded || !checkoutView) return
    if (submitting || placedOrder) return
    if (cartLines.some((line) => line.status === 'ready')) return
    emptyCartRedirectedRef.current = true
    showToast('Your cart has no items ready to order. Review it before checking out.', 'info')
    navigate('/cart')
  }, [
    loading,
    cartFetchSucceeded,
    checkoutView,
    submitting,
    placedOrder,
    cartLines,
    showToast,
    navigate,
  ])

  // UID-128 (ADR-0053 §7 follow-up) — apply the checkout shipping SEED as §1/§2's initial
  // selection, as a CHANGEABLE DEFAULT only, once BOTH the checkout view and the seed are
  // available (in either order — the seed fetch above runs independently and may resolve
  // before or after `loadCheckout`). Runs at most once per mount (`seedAppliedRef`).
  //
  // PRECEDENCE (documented per the UID-128 spec, re-affirmed by the Gate 5 QA fix that
  // decoupled the seed fetch from checkout's render): the seed overrides a field ONLY when
  // the customer has NOT made their own explicit selection of that field —
  // `methodExplicitlySelectedRef` / `addressExplicitlySelectedRef` flip `true` the moment
  // `handleSelectMethod` / `handleSelectAddress` run. Because the seed fetch is no longer
  // gated behind checkout's render, this race is now real (e.g. a slow seed resolving after
  // the customer has already picked a method) — the customer's own choice always wins.
  //
  // PR #139 review (prasad-fictiv) caught a narrower, mirror-image race: even with the
  // per-field ref guard above, a seed that resolves WHILE the customer's own `loadCheckout`
  // for a DIFFERENT field is still in flight would read `checkoutView`'s not-yet-updated
  // value for that other field and re-issue it — silently reverting the customer's pick if
  // the seed's request then resolved after theirs. `handleSelectMethod`/`handleSelectAddress`
  // both set `selectionBusy` true for the exact span of that in-flight request (before the
  // `await`, cleared after), so gating this effect on `!selectionBusy` defers the seed until
  // any explicit selection has fully landed in `checkoutView` — at which point every field
  // this effect reads is guaranteed fresh, not stale. `selectionBusy` in the dependency array
  // re-runs this effect once busy clears, so a seed that arrived mid-selection isn't dropped,
  // only deferred.
  //
  // This is a plain GET; it never writes the RFQ, and an absent/empty/failed seed (no
  // RFQ-originated cart line, the RFQ chose no shipping method, a cross-tenant/unknown
  // address dropped server-side, or the request itself failing) leaves checkout exactly as
  // it behaves today — the server's own defaults stand.
  useEffect(() => {
    if (!checkoutView || !shippingSeed || seedAppliedRef.current || selectionBusy) return
    seedAppliedRef.current = true

    const seededMethodId = methodExplicitlySelectedRef.current
      ? undefined
      : resolveSeededShippingMethodId(shippingSeed, checkoutView.shippingMethods)
    const seededAddressId = addressExplicitlySelectedRef.current
      ? undefined
      : resolveSeededShipToAddressId(shippingSeed, checkoutView.shipToAddresses)

    const methodChanged =
      seededMethodId !== undefined && seededMethodId !== checkoutView.selectedShippingMethodId
    const addressChanged =
      seededAddressId !== undefined && seededAddressId !== checkoutView.selectedShipToAddressId

    if (methodChanged || addressChanged) {
      void loadCheckout({
        shipToAddressId: seededAddressId ?? checkoutView.selectedShipToAddressId,
        shippingMethodId: seededMethodId ?? checkoutView.selectedShippingMethodId,
      })
    }
  }, [checkoutView, shippingSeed, loadCheckout, selectionBusy])

  // UID-617 — refetch the DeliveryPlanStep's stable rates whenever the actual §1/§2
  // selection changes, so "together" tracks the CURRENTLY selected method. Deliberately NOT
  // re-run on a ship-plan mode switch (`handleSelectDeliveryPlanMode` never touches
  // selectedAddressId/selectedMethodId) — that's the whole point: switching modes must never
  // change what either card itself displays, only the Order Summary's mode-dependent total.
  //
  // ONLY pass an explicit id when the buyer genuinely picked it on THIS screen
  // (methodExplicitlySelectedRef / addressExplicitlySelectedRef, same guard the RFQ-seed
  // effect above uses). `GET /checkout`'s OWN summary is never fed a synthetic explicit id —
  // it seeds from the cart's current delivery option and only overrides to the id-specific
  // price when doing so (`resolveCheckoutShippingMethod`'s "explicit-selection-wins" rule).
  // Always passing `selectedMethodId` here — even though it usually only reflects that SAME
  // seed, not a real override — defeated that rule on every fetch, so this card silently fell
  // back to the generic (non-delivery-option-specific) rate while the Order Summary kept the
  // correct one: switching the cart's delivery option (e.g. to Overseas Economy) never moved
  // this card, and it collapsed onto the same number `readyAmount` uses (both generic Ground).
  useEffect(() => {
    if (!selectedAddressId || !selectedMethodId) return
    let mounted = true
    void loadDeliveryPlanRates({
      ...(addressExplicitlySelectedRef.current ? { shipToAddressId: selectedAddressId } : {}),
      ...(methodExplicitlySelectedRef.current ? { shippingMethodId: selectedMethodId } : {}),
    }).then((rates) => {
      if (mounted) setDeliveryPlanRates(rates)
    })
    return () => {
      mounted = false
    }
  }, [selectedAddressId, selectedMethodId, loadDeliveryPlanRates])

  // If the previously-selected payment segment becomes ineligible (e.g. a
  // refreshed eligibility read after a server 422), don't leave a
  // now-disabled segment visually "selected" (UX spec §3.5).
  useEffect(() => {
    if (eligibilityStatus === 'loaded' && !allowedMethods.includes(paymentKind)) {
      const fallback = allowedMethods[0] ?? 'card'
      setPaymentKind(fallback)
      if (checkoutView) defaultPaymentSelectionFor(checkoutView, fallback)
    }
  }, [eligibilityStatus, allowedMethods, paymentKind, checkoutView, defaultPaymentSelectionFor])

  if (auth.status === 'loading') {
    return null
  }

  if (!isAuthorized) {
    return (
      <section className="checkout-screen">
        <h1 className="display-heading">Checkout isn&rsquo;t available for your role</h1>
        <p>Placing an order is available to Purchasers only.</p>
        <p>
          <Link to="/cart">Back to cart</Link>
        </p>
      </section>
    )
  }

  // ---- §1 shipping address ----
  async function handleSelectAddress(id: string) {
    // UID-128: the customer is now making their OWN explicit §1 choice — the RFQ seed
    // (see the seed-application effect) must never override this again.
    addressExplicitlySelectedRef.current = true
    if (id === selectedAddressId) return
    setSelectionBusy(true)
    await loadCheckout({ shipToAddressId: id, shippingMethodId: selectedMethodId })
    setSelectionBusy(false)
  }

  // ---- §2 shipping method ----
  async function handleSelectMethod(id: string) {
    // UID-128: the customer is now making their OWN explicit §2 choice — the RFQ seed
    // (see the mount effect) must never override this again.
    methodExplicitlySelectedRef.current = true
    if (id === selectedMethodId) return
    setSelectionBusy(true)
    await loadCheckout({ shipToAddressId: selectedAddressId, shippingMethodId: id })
    setSelectionBusy(false)
  }

  // ---- Delivery-plan step: writes through to the cart's `ShipPlan.mode` (mirrors the
  // Cart screen's `handleSelectMode`), then refreshes checkout's derived cost/summary
  // (§2's shipping-cost line) from a fresh `GET /checkout` under the current selections —
  // the same re-fetch-to-recalculate pattern every other checkout selection uses (ADR-0029:
  // the summary is never client-recomputed). ----
  async function handleSelectDeliveryPlanMode(mode: ShipMode) {
    setDeliveryPlanBusy(true)
    try {
      const { data, error } = await apiClient.PUT('/cart/ship-plan', { body: { mode } })
      if (data) {
        setCartLines(data.lines)
        setCartSplitPoEnabled(data.splitPoEnabled ?? false)
        setQuoteCart(data)
        setLineCount(combinedItemCount(data))
        await loadCheckout({
          shipToAddressId: selectedAddressId,
          shippingMethodId: selectedMethodId,
        })
        return
      }
      if (error?.status === 401) return // Handled globally.
      showToast("Couldn't update the delivery plan. Please try again.", 'error')
    } finally {
      setDeliveryPlanBusy(false)
    }
  }

  // ---- UID-97 K2 — address CRUD (ADR-0044 §2) ----
  async function handleCreateAddress(fields: AddressFormFields, isDefault = false) {
    // Don't create a duplicate: if the same physical address is already in the
    // book (street/apt/city/state/ZIP, case-insensitive), skip the POST and
    // tell the user — same guard as the cart's "Add new address".
    const norm = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase()
    const duplicate = addresses.find(
      (address) =>
        norm(address.line1) === norm(fields.line1) &&
        norm(address.line2 ?? '') === norm(fields.line2) &&
        norm(address.city) === norm(fields.city) &&
        norm(address.region) === norm(fields.region) &&
        norm(address.postalCode) === norm(fields.postalCode),
    )
    if (duplicate) {
      showToast('That address is already saved.')
      return
    }
    const { data, error } = await apiClient.POST('/account/addresses', {
      body: toCreateAddressRequest(fields, isDefault),
    })
    if (data) {
      setAddresses(data.addresses)
      return
    }
    if (error?.status === 401) return // Handled globally.
    showToast("Couldn't add that address. Please try again.", 'error')
    throw new Error('createAddress failed')
  }

  async function handleUpdateAddress(id: string, fields: AddressFormFields) {
    const { data, error } = await apiClient.PUT('/account/addresses/{addressId}', {
      params: { path: { addressId: id } },
      body: toUpdateAddressRequest(fields),
    })
    if (data) {
      setAddresses(data.addresses)
      return
    }
    if (error?.status === 401) return // Handled globally.
    showToast("Couldn't save that address. Please try again.", 'error')
    throw new Error('updateAddress failed')
  }

  async function handleDeleteAddress(id: string) {
    const { data, error } = await apiClient.DELETE('/account/addresses/{addressId}', {
      params: { path: { addressId: id } },
    })
    if (data) {
      setAddresses(data.addresses)
      // The deleted address may have been the one driving §1's selection /
      // the summary's tax line — reload the checkout view so the server
      // re-resolves a valid default rather than leaving a dangling id.
      if (id === selectedAddressId) {
        await loadCheckout({ shippingMethodId: selectedMethodId })
      }
      // Design deletes inline (no confirm dialog) and flashes a toast
      // (`Fictiv Platform.dc.html:6194` `deleteAddr` → `flash('Address removed')`).
      showToast('Address removed')
      return
    }
    if (error?.status === 401) return // Handled globally.
    showToast("Couldn't delete that address. Please try again.", 'error')
  }

  async function handleSetDefaultAddress(id: string) {
    const { data, error } = await apiClient.POST('/account/addresses/{addressId}/default', {
      params: { path: { addressId: id } },
    })
    if (data) {
      setAddresses(data.addresses)
      return
    }
    if (error?.status === 401) return // Handled globally.
    showToast("Couldn't set that address as default. Please try again.", 'error')
  }

  // UID-206 — add a saved shipping account (checkout §2). Mirrors
  // `handleCreateAddress`: POST → refresh the book from the returned fresh list,
  // auto-select the new account, keep the modal open on failure (rethrow so the
  // modal shows busy=false and stays put; the caller toasts). The full account
  // number is sent here and never echoed back.
  async function handleAddShippingAccount(fields: ShippingAccountFormFields) {
    const { data, error } = await apiClient.POST('/account/shipping-accounts', {
      body: toCreateShippingAccountRequest(fields),
    })
    if (data) {
      setShippingAccounts(data.shippingAccounts)
      setSelectedShippingAccountId(data.shippingAccountId)
      return
    }
    if (error?.status === 401) return // Handled globally.
    showToast("Couldn't add that shipping account. Please try again.", 'error')
    throw new Error('createShippingAccount failed')
  }

  // ---- UID-97 K3 — saved-payment removal (ADR-0044 §2) ----
  async function handleRemoveSavedPayment(id: string) {
    const { data, error } = await apiClient.DELETE('/account/payment-methods/{paymentMethodId}', {
      params: { path: { paymentMethodId: id } },
    })
    if (data) {
      setPaymentMethodsOverride(data.paymentMethods)
      if (id === selectedSavedPaymentId) {
        setSelectedSavedPaymentId(undefined)
        setPaymentMode('new')
      }
      // Design removes inline (no confirm dialog) and flashes a toast
      // (`Fictiv Platform.dc.html:6203` `deleteCard` → `flash('Card removed')`).
      showToast('Payment method removed')
      return
    }
    if (error?.status === 401) return // Handled globally.
    showToast("Couldn't remove that payment method. Please try again.", 'error')
  }

  function handlePoGroupNumberChange(groupKey: string, value: string) {
    setPoGroupNumbers((current) => ({ ...current, [groupKey]: value }))
    // Clear just this group's error as the buyer fills it in — mirrors the
    // single-field pattern (`handlePoNumberChange`) rather than re-running
    // full validation on every keystroke.
    if (poGroupErrors[groupKey]) {
      setPoGroupErrors((current) => {
        const next = { ...current }
        delete next[groupKey]
        return next
      })
    }
  }

  // ---- §3 payment: tab + saved/new selection ----
  function handlePaymentKindChange(kind: PaymentMethodKind) {
    setPaymentKind(kind)
    if (checkoutView) defaultPaymentSelectionFor(checkoutView, kind)
  }

  // ---- §3 card ----
  function handleCardFieldChange(field: keyof CardFormFields, value: string) {
    const next = { ...card, [field]: value }
    setCard(next)
    if (cardTouched.has(field)) {
      setCardErrors(validateCardFields(next))
    }
  }

  function handleCardFieldBlur(field: keyof CardFormFields) {
    setCardTouched((current) => new Set(current).add(field))
    setCardErrors(validateCardFields(card))
  }

  // ---- §3 ACH ----
  function handleAchFieldChange(field: keyof AchFormFields, value: string) {
    const next = { ...ach, [field]: value } as AchFormFields
    setAch(next)
    if (achTouched.has(field)) {
      setAchErrors(validateAchFields(next))
    }
  }

  function handleAchFieldBlur(field: keyof AchFormFields) {
    setAchTouched((current) => new Set(current).add(field))
    setAchErrors(validateAchFields(ach))
  }

  // ---- §3 PO ----
  function handlePoNumberChange(value: string) {
    setPoNumber(value)
    if (poTouched) {
      setPoErrors(validatePoFields(value))
    }
  }

  function handlePoNumberBlur() {
    setPoTouched(true)
    setPoErrors(validatePoFields(poNumber))
  }

  // ---- Generate quote ----
  // Opens the shared `GenerateQuoteModal` (which owns the whole confirm →
  // generate → ready flow, including its own `POST /checkout/quote`). The cart
  // is normally already loaded on mount (`loadCartLines`), so this opens
  // immediately; if it hasn't arrived yet, fetch it once, then open.
  async function handleGenerateQuote() {
    if (generatingQuote) return
    if (quoteCart) {
      setShowGenerateQuote(true)
      return
    }
    setGeneratingQuote(true)
    try {
      const { data } = await apiClient.GET('/cart')
      if (data) {
        setCartLines(data.lines)
        setQuoteCart(data)
        setShowGenerateQuote(true)
        return
      }
      showToast("Couldn't open the quote right now. Please try again.", 'error')
    } catch {
      showToast("Couldn't open the quote right now. Please try again.", 'error')
    } finally {
      setGeneratingQuote(false)
    }
  }

  // ---- Place order ----
  function handlePlaceOrder() {
    if (!termsAccepted) {
      termsCheckboxRef.current?.focus()
      setLiveMessage('Accept the terms above to place your order.')
      return
    }
    if (submitting) return

    // UID-206 — own-account chosen with an empty book: there is no account to
    // resolve into the carrier/ref, so block here rather than round-tripping to
    // a 422. Direct the buyer to add one (or switch to prepaid).
    if (ownAccountMissingShippingAccount()) {
      setPlacementBannerShowOrdersLink(false)
      setPlacementBanner(
        'Add a shipping account to bill this order to, or choose Fictiv prepaid shipping.',
      )
      setLiveMessage('Add a shipping account to place your order.')
      return
    }

    if (paymentKind === 'card' && paymentMode === 'new') {
      const errors = validateCardFields(card)
      if (Object.values(errors).some(Boolean)) {
        setCardErrors(errors)
        setCardTouched(new Set(CARD_FIELD_ORDER))
        focusFirstError(CARD_FIELD_ORDER, errors)
        announceErrors(errors)
        return
      }
    } else if (paymentKind === 'ach' && paymentMode === 'new') {
      const errors = validateAchFields(ach)
      if (Object.values(errors).some(Boolean)) {
        setAchErrors(errors)
        setAchTouched(new Set(ACH_FIELD_ORDER))
        focusFirstError(ACH_FIELD_ORDER, errors)
        announceErrors(errors)
        return
      }
    } else if (paymentKind === 'po_invoice' && splitEnabled && canSplit) {
      // Gate 5 C1 — every group's PO is REQUIRED for this payment kind
      // specifically (unlike a non-PO-invoice split, where a group PO stays
      // optional/nullable on the wire): paying by PO invoice with a blank
      // group would either fail the BFF's ajv `poNumber minLength:1` check
      // (no group to fall back to) or silently borrow a DIFFERENT group's PO
      // as the shared fallback (`firstNonBlankPoReference`, below) — neither
      // is acceptable, so block in the UI instead.
      const errors = validatePoGroupNumbers(poGroups, poGroupNumbers)
      if (Object.values(errors).some(Boolean)) {
        setPoGroupErrors(errors)
        focusFirstError(
          poGroups.map((group) => group.key),
          errors,
        )
        announceErrors(errors)
        return
      }
    } else if (paymentKind === 'po_invoice') {
      // The single PO-number field is required (split is off, or the cart
      // isn't split-eligible) — matching the prototype's single `poSingle`
      // field.
      const errors = validatePoFields(poNumber)
      if (Object.values(errors).some(Boolean)) {
        setPoErrors(errors)
        setPoTouched(true)
        focusFirstError(['poNumber'], errors)
        announceErrors(errors)
        return
      }
    }

    if (splitEnabled && canSplit) {
      void submitBatchOrder()
    } else {
      void submitOrder()
    }
  }

  /** Resolves the §3 payment selection into the shared id-or-value shape
   * both `CheckoutRequest` and `PlaceOrdersRequest` carry — used by both
   * `submitOrder` (single) and `submitBatchOrder` (K1 split) so the
   * PCI-boundary token/masked-label derivation is written exactly once. */
  function resolvePaymentSelection(): {
    readonly payment?: PaymentSelection
    readonly savedPaymentMethodId?: string
  } {
    if (paymentKind === 'card') {
      if (paymentMode === 'saved' && selectedSavedPaymentId) {
        return { savedPaymentMethodId: selectedSavedPaymentId }
      }
      return {
        payment: {
          kind: 'card',
          tokenRef: generateSimulatedTokenRef(),
          displayLabel: maskedDisplayLabel(card.cardNumber),
        },
      }
    }
    if (paymentKind === 'ach') {
      if (paymentMode === 'saved' && selectedSavedPaymentId) {
        return { savedPaymentMethodId: selectedSavedPaymentId }
      }
      return {
        payment: {
          kind: 'ach',
          tokenRef: generateSimulatedTokenRef(),
          displayLabel: maskedDisplayLabel(ach.accountNumber),
        },
      }
    }
    if (splitEnabled && canSplit) {
      // Gate 5 C1 — split + po_invoice: the single `poNumber` field is
      // hidden (PaymentStep renders the per-group inputs instead), so it is
      // ALWAYS blank here; sending it as `payment.poNumber` would fail the
      // BFF's ajv `minLength:1` check on the shared `PaymentSelection`
      // before the handler even runs (a buyer could never place a split PO
      // order). `handlePlaceOrder`'s validation above (`validatePoGroupNumbers`)
      // guarantees every group has a non-blank PO before this function is
      // ever called in split mode, so `firstNonBlankPoReference` is safe to
      // treat as always-defined here — it is a CONTRACT-SATISFYING fallback
      // value only; each ORDER's own PO still comes from its group's
      // `poReference` (the BFF prefers the group PO over this shared one,
      // ADR-0044 §1), so per-order PO carry is unaffected.
      return {
        payment: {
          kind: 'po_invoice',
          poNumber: firstNonBlankPoReference(poGroups, poGroupNumbers) ?? '',
        },
      }
    }
    return { payment: { kind: 'po_invoice', poNumber } }
  }

  // UID-206 — the shipping account to bill an own-account order on. Sent ONLY
  // when the selected method is own_account AND an account is chosen; the server
  // ignores it for prepay and resolves it into the frozen carrier/ref otherwise.
  // Returns undefined so callers can omit the key (exactOptionalPropertyTypes).
  function resolveShippingAccountId(): string | undefined {
    if (!checkoutView) return undefined
    const ownAccountId = checkoutView.shippingMethods.find(
      (method) => method.kind === 'own_account',
    )?.id
    if (!ownAccountId || selectedMethodId !== ownAccountId) return undefined
    return selectedShippingAccountId ?? undefined
  }

  // UID-206 — own-account is selected but no account is available to bill on:
  // the book auto-selects its default/first on load, so a null selection while
  // own-account is chosen means the book is EMPTY. Placing anyway sends no
  // shippingAccountId and PR A's resolver fails closed with a 422 — block it in
  // the UI with inline guidance instead (mirrors the termsAccepted gate).
  function ownAccountMissingShippingAccount(): boolean {
    if (!checkoutView) return false
    const ownAccountId = checkoutView.shippingMethods.find(
      (method) => method.kind === 'own_account',
    )?.id
    return Boolean(ownAccountId) && selectedMethodId === ownAccountId && !selectedShippingAccountId
  }

  /** Shared 4xx handling for both placement endpoints (§3.5 banner variants
   * + UID-97 K1's `invalid_grouping`). `context` distinguishes the single
   * (`submitOrder`) vs batch (`submitBatchOrder`) caller — Gate 5 W2: a
   * BATCH `no_ready_lines` reads very differently from a single-order one. */
  function handlePlacementError(
    problemType: string | undefined,
    context: 'single' | 'batch' = 'single',
  ) {
    const kind = classifyCheckoutPlacementError(problemType)
    setPlacementBannerShowOrdersLink(false)
    if (kind === 'payment_method_not_eligible') {
      setPlacementBanner(
        'This payment method is no longer available for your account. Choose Credit card or ACH transfer to continue.',
      )
      void loadEligibility()
    } else if (kind === 'terms_not_accepted') {
      setPlacementBanner('Please confirm you accept the terms before placing your order.')
      termsCheckboxRef.current?.focus()
    } else if (kind === 'no_ready_lines' && context === 'batch') {
      // Gate 5 W2: a batch's ready lines are claimed by the FIRST order
      // written inside the shared per-account critical section (ADR-0044
      // §1's partial-unique-index idempotency) — a double-submitted batch's
      // LOSING request sees an already-emptied cart and gets this SAME
      // 422, even though the buyer's first batch actually succeeded. Saying
      // "your cart has no items" here would be actively misleading —
      // instead, refresh the cart/checkout state and point the buyer at
      // Recent Orders rather than implying nothing was placed.
      setPlacementBanner(
        'It looks like this order already went through — check Recent Orders for your placed order(s) before submitting again.',
      )
      setPlacementBannerShowOrdersLink(true)
      void loadCartLines()
      void loadCheckout({ shipToAddressId: selectedAddressId, shippingMethodId: selectedMethodId })
    } else if (kind === 'no_ready_lines') {
      setPlacementBanner('Your cart has no items ready to order. Return to your cart to review it.')
    } else if (kind === 'invalid_grouping') {
      // UID-97 K1: the client-derived PO groups no longer partition the
      // cart's ready lines exactly (e.g. a line changed status between load
      // and submit) — reload BOTH the groups and the checkout view rather
      // than resubmitting a stale partition.
      setPlacementBanner(
        "Your cart changed since these groups were built — we've refreshed them. Please review and place your order again.",
      )
      void loadCartLines()
      void loadCheckout({ shipToAddressId: selectedAddressId, shippingMethodId: selectedMethodId })
    } else if (kind === 'invalid_selection') {
      setPlacementBanner('One of your selections is no longer available. Reloading your options.')
      void loadCheckout({
        shipToAddressId: selectedAddressId,
        shippingMethodId: selectedMethodId,
      })
    } else {
      showToast('Something went wrong placing your order. Please try again.', 'error')
    }
  }

  async function submitOrder() {
    if (!selectedAddressId || !selectedMethodId) {
      showToast('Select a shipping address and method before placing your order.', 'error')
      return
    }

    const { payment, savedPaymentMethodId } = resolvePaymentSelection()
    // UID-206 — carry the chosen own-account shipping account (own-account only).
    const shippingAccountId = resolveShippingAccountId()
    const ownAccount = shippingAccountId ? { shippingAccountId } : {}
    const body: CheckoutRequest = savedPaymentMethodId
      ? {
          shipToAddressId: selectedAddressId,
          shippingMethodId: selectedMethodId,
          savedPaymentMethodId,
          ...ownAccount,
          // The actual accepted state (not a literal) — handlePlaceOrder already
          // guards `!termsAccepted`, but decoupling the wire value from state was a
          // latent trap that made the server's terms_not_accepted path dead (Gate 5 M1).
          termsAccepted,
        }
      : {
          shipToAddressId: selectedAddressId,
          shippingMethodId: selectedMethodId,
          payment: payment as NonNullable<typeof payment>,
          ...ownAccount,
          termsAccepted,
        }

    setSubmitting(true)
    setPlacementBanner(undefined)
    setPlacementBannerShowOrdersLink(false)
    try {
      const { data, error } = await apiClient.POST('/orders', { body })
      if (data) {
        setPlacedOrder(data)
        // Server already settled the ordered lines out of the cart — sync the
        // local cart snapshot + the top-bar badge so it doesn't show a stale count.
        void refreshCartAfterPlacement()
        return
      }
      if (error?.status === 401) return // Handled globally.
      handlePlacementError(error?.type, 'single')
    } catch {
      showToast('Something went wrong placing your order. Please try again.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  /** UID-97 K1 — split-PO batch placement (`POST /orders/batch`,
   * ADR-0044 §1). The shared shipping/payment/terms selections are
   * identical to `submitOrder`'s; only the request shape (`groups[]`
   * instead of one selection) and the success handling (N `OrderDetail`s +
   * a server-derived `poBreakdown`) differ. */
  async function submitBatchOrder() {
    if (!selectedAddressId || !selectedMethodId) {
      showToast('Select a shipping address and method before placing your order.', 'error')
      return
    }

    const { payment, savedPaymentMethodId } = resolvePaymentSelection()
    const groups = buildOrderGroups(poGroups, poGroupNumbers)
    // UID-206 — carry the chosen own-account shipping account (applies to all
    // orders in the batch; own-account only).
    const shippingAccountId = resolveShippingAccountId()
    const ownAccount = shippingAccountId ? { shippingAccountId } : {}
    const body: PlaceOrdersRequest = savedPaymentMethodId
      ? {
          shipToAddressId: selectedAddressId,
          shippingMethodId: selectedMethodId,
          savedPaymentMethodId,
          ...ownAccount,
          termsAccepted,
          groups,
        }
      : {
          shipToAddressId: selectedAddressId,
          shippingMethodId: selectedMethodId,
          payment: payment as NonNullable<typeof payment>,
          ...ownAccount,
          termsAccepted,
          groups,
        }

    setSubmitting(true)
    setPlacementBanner(undefined)
    setPlacementBannerShowOrdersLink(false)
    try {
      const { data, error } = await apiClient.POST('/orders/batch', { body })
      if (data) {
        handleBatchPlaced(data)
        return
      }
      if (error?.status === 401) return // Handled globally.
      handlePlacementError(error?.type, 'batch')
    } catch {
      showToast('Something went wrong placing your order. Please try again.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  function handleBatchPlaced(result: PlaceOrdersResult) {
    const [firstOrder] = result.orders
    if (!firstOrder) return
    setPlacedOrder(firstOrder)
    setPlacedPoBreakdown(result.poBreakdown)
    // Collapse the split-PO UI after placement (the ordered ready lines are gone, so the
    // fresh cart will resolve to no split anyway). UID-315: reset the carried choice locally.
    setCartSplitPoEnabled(false)
    setPoGroupNumbers({})
    // Server already settled every ordered line across the batch — sync the
    // local cart snapshot + the top-bar badge so it doesn't show a stale count.
    void refreshCartAfterPlacement()
  }

  if (loading) {
    return (
      <section aria-labelledby="checkout-heading" className="checkout-screen">
        <h1 id="checkout-heading" className="display-heading">
          Checkout
        </h1>
        <Spinner label="Loading checkout" />
      </section>
    )
  }

  if (loadError && !checkoutView) {
    return (
      <section aria-labelledby="checkout-heading" className="checkout-screen">
        <h1 id="checkout-heading" className="display-heading">
          Checkout
        </h1>
        <div role="alert" className="empty-state-panel">
          <p className="empty-state-title">Couldn&rsquo;t load checkout</p>
          <p className="empty-state-detail">{loadError}</p>
          <div className="empty-state-action">
            <Button variant="secondary" onClick={() => void loadCheckout()}>
              Try again
            </Button>
          </div>
        </div>
      </section>
    )
  }

  if (!checkoutView || !selectedAddressId || !selectedMethodId) {
    return null
  }

  // UID-617 — DeliveryPlanStep's two cards need TWO mode-INDEPENDENT shipping figures, not
  // `checkoutView.summary.shippingAmount` (mode-DEPENDENT on purpose). `deliveryPlanRates`
  // (`GET /checkout/delivery-plan-rates`) is the real, backend-computed source (see
  // `deriveDeliveryPlanRates`); the local fallback below only covers the brief window before
  // that fetch resolves (or a failed fetch), and is itself now safe to read from
  // `checkoutView.shippingMethods` since the backend price-override bug that made every
  // method collapse onto the same number (missing methodId guard in `resolveShippingCost`)
  // is fixed — it's a stand-in, not a second source of truth.
  const selectedShippingMethod = checkoutView.shippingMethods.find((m) => m.id === selectedMethodId)
  const deliveryPlanHasStandardShipment = cartLines.some(
    (line) => line.sourceable.kind === 'catalog',
  )
  const groundMethod = checkoutView.shippingMethods.find((m) => m.id === 'ship-ground')
  const deliveryPlanTogetherAmount =
    deliveryPlanRates?.togetherAmount ??
    selectedShippingMethod?.costAmount ??
    checkoutView.summary.shippingAmount
  const deliveryPlanReadyAmount =
    deliveryPlanRates?.readyAmount ??
    (deliveryPlanHasStandardShipment ? (groundMethod?.costAmount ?? '0.00') : '0.00')

  // §1 renders from the K2 address-book fetch (`AddressView[]`, carries K4's
  // contactName/companyName) once it has loaded. Until/unless that fetch
  // resolves (or on any account-addresses error, which `loadAddresses`
  // treats as non-fatal), fall back to `checkoutView.shipToAddresses` —
  // the SAME tenant list, minus the two K4 fields — with contactName/
  // companyName explicitly `null` (a documented placeholder, never
  // fabricated) rather than leaving §1 empty.
  const addressCardsBase: readonly AddressView[] =
    addresses.length > 0
      ? addresses
      : checkoutView.shipToAddresses.map((address) => ({
          ...address,
          contactName: null,
          companyName: null,
        }))
  // Default ship-to always renders first ("at top"). `Array.prototype.sort` is
  // stable (ES2019+), so every non-default address keeps its original order.
  const addressCards: readonly AddressView[] = [...addressCardsBase].sort(
    (a, b) => Number(b.isDefault) - Number(a.isDefault),
  )

  const savedPaymentMethods = paymentMethodsOverride ?? checkoutView.savedPaymentMethods

  return (
    <section aria-labelledby="checkout-heading" className="checkout-screen">
      <p>
        <Link to="/cart" className="checkout-back-link">
          <LeftOutlined aria-hidden="true" className="checkout-back-link-icon" />
          Back to cart
        </Link>
      </p>
      <h1 id="checkout-heading" className="display-heading">
        Checkout
      </h1>
      <p role="alert" className="sr-only">
        {liveMessage}
      </p>

      {/* Placement/selection errors (422/409) render here, above the
          sections, so they are visible regardless of scroll position — every
          section is expanded at once on the single page. */}
      {placementBanner && (
        <div role="alert" className="checkout-error-banner">
          <p>{placementBanner}</p>
          {placementBannerShowOrdersLink && <Link to="/orders">View Recent Orders</Link>}
        </div>
      )}

      <div className="checkout-layout">
        <div className="checkout-sections-col">
          <CheckoutSection
            number={addressStepNumber}
            heading="Where should we ship these parts?"
            headingId="checkout-section-1-heading"
          >
            <ShippingAddressStep
              addresses={addressCards}
              selectedId={selectedAddressId}
              onSelect={(id) => void handleSelectAddress(id)}
              busy={selectionBusy}
              onCreate={handleCreateAddress}
              onUpdate={handleUpdateAddress}
              onDelete={handleDeleteAddress}
              onSetDefault={handleSetDefaultAddress}
            />
          </CheckoutSection>

          {showDeliveryPlan && (
            <CheckoutSection
              number={deliveryPlanStepNumber}
              heading="How should these parts be delivered?"
              headingId="checkout-section-delivery-plan-heading"
            >
              <DeliveryPlanStep
                lines={cartLines}
                {...(linePresentationsBySourceableId
                  ? { presentationsBySourceableId: linePresentationsBySourceableId }
                  : {})}
                thumbnailsBySourceableId={thumbnailsBySourceableId}
                shipPlan={quoteCart?.shipPlan}
                onSelectMode={(mode) => void handleSelectDeliveryPlanMode(mode)}
                busy={deliveryPlanBusy}
                shippingAmount={deliveryPlanTogetherAmount}
                readyShippingAmount={deliveryPlanReadyAmount}
                currency={checkoutView.summary.currency}
              />
            </CheckoutSection>
          )}

          <CheckoutSection
            number={shippingMethodStepNumber}
            heading="How would you like to ship these parts?"
            headingId="checkout-section-2-heading"
          >
            <ShippingMethodStep
              methods={checkoutView.shippingMethods}
              selectedId={selectedMethodId}
              onSelect={(id) => void handleSelectMethod(id)}
              busy={selectionBusy}
              seededShipAccountNumber={shippingSeed?.shipAccountNumber ?? null}
              shippingAccounts={shippingAccounts}
              selectedShippingAccountId={selectedShippingAccountId}
              onSelectShippingAccount={setSelectedShippingAccountId}
              onAddShippingAccount={() => setShowAddShippingAccount(true)}
            />
          </CheckoutSection>

          <CheckoutSection
            number={paymentStepNumber}
            heading="How would you like to pay?"
            headingId="checkout-section-3-heading"
          >
            <PaymentStep
              eligibilityStatus={eligibilityStatus}
              allowedMethods={allowedMethods}
              kind={paymentKind}
              onKindChange={handlePaymentKindChange}
              savedPaymentMethods={savedPaymentMethods}
              mode={paymentMode}
              selectedSavedId={selectedSavedPaymentId}
              onModeChange={setPaymentMode}
              onSelectSaved={setSelectedSavedPaymentId}
              onRemoveSaved={handleRemoveSavedPayment}
              card={card}
              onCardFieldChange={handleCardFieldChange}
              onCardFieldBlur={handleCardFieldBlur}
              cardErrors={cardErrors}
              ach={ach}
              onAchFieldChange={handleAchFieldChange}
              onAchFieldBlur={handleAchFieldBlur}
              achErrors={achErrors}
              poNumber={poNumber}
              onPoNumberChange={handlePoNumberChange}
              onPoNumberBlur={handlePoNumberBlur}
              poErrors={poErrors}
              poGroups={splitEnabled && canSplit ? poGroups : undefined}
              poGroupNumbers={poGroupNumbers}
              onPoGroupNumberChange={handlePoGroupNumberChange}
              poGroupErrors={poGroupErrors}
              registerFieldRef={registerFieldRef}
            />
          </CheckoutSection>
        </div>

        <CheckoutSummaryRail
          summary={checkoutView.summary}
          termsAccepted={termsAccepted}
          onTermsChange={setTermsAccepted}
          termsCheckboxRef={termsCheckboxRef}
          onPlaceOrder={handlePlaceOrder}
          submitting={submitting}
          onGenerateQuote={() => void handleGenerateQuote()}
          generatingQuote={generatingQuote}
          placeOrderLabel={placeOrderCtaLabel(splitEnabled && canSplit ? poGroups.length : 1)}
          canSplit={canSplit}
          splitNote={
            splitEnabled && canSplit ? placingPurchaseOrdersNote(poGroups.length) : undefined
          }
          readinessSplitNote={readinessSplitNote}
        />
      </div>

      {/* UID-139/UID-431 — the same rich quote modal the cart uses. Generating a quote here
          creates the RFQ and clears the cart server-side (share-quote create branch), so the
          ready state's "Proceed to checkout" has nothing left to check out: it zeroes the badge
          and lands the buyer on the (now-empty) cart page rather than this stale checkout page.
          Mounted only while open (and once the cart has loaded) — the modal reads `cart.summary`
          eagerly, so it must never mount without a real `CartView`. */}
      {showGenerateQuote && quoteCart && (
        <GenerateQuoteModal
          cart={quoteCart}
          open
          onClose={() => setShowGenerateQuote(false)}
          onProceedToCheckout={() => {
            setLineCount(0)
            navigate('/cart')
          }}
        />
      )}

      <AddShippingAccountModal
        open={showAddShippingAccount}
        onClose={() => setShowAddShippingAccount(false)}
        onSubmit={handleAddShippingAccount}
      />

      {placedOrder && (
        <OrderPlacedCelebration
          orderId={placedOrder.id}
          onViewOrder={() => navigate(`/orders/${placedOrder.id}`)}
          poBreakdown={placedPoBreakdown}
        />
      )}
    </section>
  )
}
